import { Brain, ChevronDown, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";

interface ThinkingBlockProps {
  content: string;
  isStreaming: boolean;
  /** Initial expanded state (live = open, persisted history = collapsed). */
  defaultOpen?: boolean;
  /** When this flips truthy, auto-collapse once (the answer started). */
  collapseSignal?: boolean;
}

/**
 * Inner-thought widget. Renders the agent's reasoning (ZeroClaw `thinking`
 * frames) below the user prompt and above the answer. Auto-collapses when the
 * answer arrives; persisted copies render collapsed. User can re-expand.
 */
export function ThinkingBlock({
  content,
  isStreaming,
  defaultOpen = true,
  collapseSignal = false,
}: ThinkingBlockProps) {
  const [open, setOpen] = useState(defaultOpen);
  const prevCollapse = useRef(collapseSignal);
  useEffect(() => {
    if (collapseSignal && !prevCollapse.current) setOpen(false);
    prevCollapse.current = collapseSignal;
  }, [collapseSignal]);

  return (
    <div className="my-2 rounded-lg border border-black/10 bg-black/[0.02] dark:border-white/10 dark:bg-white/[0.02]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-xs font-medium text-black/50 dark:text-white/50"
      >
        <Brain className="h-3 w-3 shrink-0" />
        <span>Thinking</span>
        {isStreaming && (
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-brand-coral" />
        )}
        {open ? (
          <ChevronDown className="ml-auto h-3 w-3 shrink-0" />
        ) : (
          <ChevronRight className="ml-auto h-3 w-3 shrink-0" />
        )}
      </button>
      {open && (
        <div className="max-h-48 overflow-y-auto whitespace-pre-wrap px-3 pb-2 font-mono text-xs leading-relaxed text-black/45 dark:text-white/45">
          {content}
        </div>
      )}
    </div>
  );
}
